import React from 'react';
import Dialog from '@mui/material/Dialog';
import Button from '@mui/material/Button';
import { printPickLists } from '../marketplace-bins/pickList';

const numberOrNull = (value) => {
  if (value == null || value === '') return null;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
};

const displayCount = (value) => {
  const parsed = numberOrNull(value);
  return parsed == null ? '—' : String(parsed);
};

const money = (value) => {
  const parsed = numberOrNull(value);
  if (parsed == null) return '';
  return `$${parsed.toFixed(2)}`;
};

const stockStatus = (bin) => {
  const boh = numberOrNull(bin && bin.marketplace_boh);
  const min = numberOrNull(bin && bin.marketplace_min);
  const max = numberOrNull(bin && bin.marketplace_max);
  if (boh == null) {
    return { label: 'Unknown', color: '#64748b', background: '#f1f5f9' };
  }
  if (boh <= 0 && (max == null || max > 0)) {
    return { label: 'Out of stock', color: '#9f1239', background: '#ffe4e6' };
  }
  if (min != null && boh < min) {
    return { label: 'Below minimum', color: '#9a3412', background: '#ffedd5' };
  }
  return { label: 'In stock', color: '#166534', background: '#dcfce7' };
};

const ShopBinDialog = ({ bin, onClose }) => {
  const open = Boolean(bin);
  const status = stockStatus(bin);
  const boh = numberOrNull(bin && bin.marketplace_boh);
  const max = numberOrNull(bin && bin.marketplace_max);
  const reserved = numberOrNull(bin && bin.marketplace_reserved) || 0;
  const available = boh == null ? null : Math.max(0, boh - reserved);
  const room = boh == null || max == null ? null : Math.max(0, max - boh);
  const price = money(bin && bin.marketplace_price);
  const upc = (bin && bin.marketplace_upc) || '';
  const itemNumber = (bin && bin.marketplace_item_number) || '';
  const loadCode = (bin && bin.marketplace_load_code) || '';
  const loadQty = numberOrNull(bin && bin.marketplace_load_qty);
  const printQty = loadQty != null ? loadQty : room;
  const facts = [
    upc ? `UPC ${upc}` : '',
    itemNumber ? `Item ${itemNumber}` : '',
    price ? `${price}${bin && bin.marketplace_uom ? ` / ${bin.marketplace_uom}` : ''}` : '',
  ].filter(Boolean);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      aria-labelledby="marketplace-bin-title"
      PaperProps={{
        sx: {
          width: 460,
          maxWidth: 'calc(100% - 32px)',
          borderRadius: '14px',
          overflow: 'hidden',
        },
      }}
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'flex-start',
          justifyContent: 'space-between',
          gap: 16,
          padding: '18px 20px',
          background: '#0a5d66',
          color: '#fff',
        }}
      >
        <div>
          <div style={{ fontSize: 12, letterSpacing: '0.08em', fontWeight: 700, opacity: 0.85 }}>
            SHOP
          </div>
          <h2 id="marketplace-bin-title" style={{ margin: '4px 0 0', fontSize: 22, fontWeight: 700 }}>
            Bin {bin && bin.bin_id ? bin.bin_id : '—'}
          </h2>
        </div>
        <span
          style={{
            marginTop: 4,
            padding: '4px 10px',
            borderRadius: 999,
            background: status.background,
            color: status.color,
            fontSize: 12,
            fontWeight: 700,
            whiteSpace: 'nowrap',
          }}
        >
          {status.label}
        </span>
      </div>
      <div style={{ padding: '18px 20px 8px', background: '#f7fafa' }}>
        <div style={{ fontSize: 18, fontWeight: 700, color: '#102a2e', lineHeight: 1.3 }}>
          {(bin && bin.marketplace_product) || 'Marketplace item'}
        </div>
        {facts.length ? (
          <div style={{ marginTop: 6, color: '#5b6b70', fontSize: 13 }}>{facts.join('  ·  ')}</div>
        ) : null}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 10, marginTop: 16 }}>
          {[
            ['On hand', displayCount(bin && bin.marketplace_boh)],
            ['Minimum', displayCount(bin && bin.marketplace_min)],
            ['Maximum', displayCount(bin && bin.marketplace_max)],
          ].map(([label, value]) => (
            <div
              key={label}
              style={{
                background: '#fff',
                border: '1px solid #e4ecee',
                borderRadius: 10,
                padding: '10px 12px',
              }}
            >
              <div style={{ fontSize: 12, color: '#5b6b70' }}>{label}</div>
              <div style={{ marginTop: 2, fontSize: 22, fontWeight: 700, color: '#0a5d66' }}>{value}</div>
            </div>
          ))}
        </div>
        <div style={{ display: 'flex', gap: 16, marginTop: 12, fontSize: 13, color: '#334155' }}>
          <span>Available to sell <strong>{displayCount(available)}</strong></span>
          <span>Room to load <strong>{displayCount(room)}</strong></span>
        </div>
        {loadCode ? (
          <div
            style={{
              marginTop: 14,
              padding: '10px 12px',
              borderRadius: 10,
              background: '#fff',
              border: '1px solid #e4ecee',
              fontSize: 13,
              color: '#334155',
            }}
          >
            Open load code <strong style={{ color: '#0a5d66' }}>{loadCode}</strong>
          </div>
        ) : null}
        <div
          style={{
            marginTop: 14,
            padding: '10px 12px',
            borderRadius: 10,
            background: '#e7f4f5',
            color: '#0a5d66',
            fontSize: 13,
            lineHeight: 1.45,
          }}
        >
          This bin is reserved for Shop and cannot take a prescription.
        </div>
      </div>
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          gap: 12,
          padding: '12px 20px 16px',
          background: '#f7fafa',
        }}
      >
        <Button
          onClick={() => {
            if (!loadCode) return;
            printPickLists([{
              order_code: loadCode,
              lines: [{
                bin_id: bin && bin.bin_id,
                product_name: bin && bin.marketplace_product,
                suggested_qty: printQty == null ? '' : printQty,
                boh: bin && bin.marketplace_boh,
                min_qty: bin && bin.marketplace_min,
                max_qty: bin && bin.marketplace_max,
              }],
            }]);
          }}
          variant="outlined"
          disabled={!loadCode}
          sx={{
            textTransform: 'none',
            color: '#0a5d66',
            borderColor: '#0a5d66',
            borderRadius: '8px',
            fontWeight: 700,
            '&:hover': { borderColor: '#084b52', backgroundColor: '#e7f4f5' },
          }}
        >
          Print refill
        </Button>
        <Button
          onClick={onClose}
          variant="contained"
          sx={{
            textTransform: 'none',
            backgroundColor: '#0a5d66',
            borderRadius: '8px',
            fontWeight: 700,
            '&:hover': { backgroundColor: '#084b52' },
          }}
        >
          Close
        </Button>
      </div>
    </Dialog>
  );
};

export default ShopBinDialog;



// WEBPACK FOOTER //
// ./src/components/assign-prescriptions/ShopBinDialog.js